import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import './global/VerificationPolicy.css'; // Reuse the same CSS

function VerificationPolicy() {
  return (
    <div className="verification-policy-page">
      <Helmet>
        <title>Verification Policy | OriginSelect Canada</title>
        <meta 
          name="description" 
          content="Learn how OriginSelect verifies Canadian brand claims including Women-Owned, Indigenous-Owned, B-Corp, Organic, Fair Trade, and other certifications. Transparent verification process for values-led Canadian brands." 
        />
        <link rel="canonical" href="https://www.originselect.com/country/canada/verification-policy" />
      </Helmet>

      <div className="policy-container">
        <div className="policy-header">
          <h1>Verification Policy</h1>
          <p className="last-updated">Last updated: Nov 2025</p>
        </div>

        <section className="tldr-section">
          <h2>TL;DR</h2>
          <p>
            OriginSelect highlights values-led Canadian brands. Some claims (e.g., B-Corp, Canada Organic) are certifications 
            issued by third parties. Other claims (e.g., Women-Owned, Indigenous-Owned) are ownership/identity attributes we verify 
            with documentation. If we haven't verified a claim yet, we label it <strong>Self-reported</strong>. 
            We never sell verification. Pro brands receive priority review, but final decisions remain editorial.
          </p>
        </section>

        <section className="badge-meanings">
          <h2>What our badges mean</h2>
          
          <div className="badge-definition">
            <div className="badge-header">
              <span className="badge-icon certified">✓</span>
              <h3>Certified</h3>
            </div>
            <p>
              The claim is governed by an external program and appears in that program's public registry, or 
              we've reviewed an official certificate (e.g., B-Corp Certified, Canada Organic, Fair Trade Canada, 
              Climate Neutral Certified).
            </p>
            <p className="schema-tag">Schema: <code>certified</code></p>
          </div>

          <div className="badge-definition">
            <div className="badge-header">
              <span className="badge-icon verified">◻︎✓</span>
              <h3>Verified by OriginSelect</h3>
            </div>
            <p>
              We reviewed evidence that reasonably substantiates the claim (e.g., legal/registration documents, 
              government-issued letters, program acceptance emails, or public company filings). Used for claims 
              like Women-Owned, Indigenous-Owned, Minority-Owned when not tied to a formal certification.
            </p>
            <p className="schema-tag">Schema: <code>verified_by_os</code></p>
          </div>

          <div className="badge-definition">
            <div className="badge-header">
              <span className="badge-icon self-reported">ℹ︎</span>
              <h3>Self-reported</h3>
            </div>
            <p>
              The brand asserts the claim but we have not yet reviewed supporting evidence, or the claim is 
              subjective (e.g., "clean beauty").
            </p>
            <p className="schema-tag">Schema: <code>self_reported</code></p>
          </div>

          <div className="badge-definition">
            <div className="badge-header">
              <span className="badge-icon pending">⏱</span>
              <h3>Pending</h3>
            </div>
            <p>We're actively reviewing submitted evidence.</p>
          </div>

          <div className="badge-definition">
            <div className="badge-header">
              <span className="badge-icon expired">⚠︎</span>
              <h3>Expired</h3>
            </div>
            <p>
              Previously verified/certified, but the evidence/certification is past its valid date or can 
              no longer be confirmed.
            </p>
          </div>
        </section>

        <section className="display-section">
          <h2>Display on brand pages</h2>
          <ul className="icon-list">
            <li><span className="icon certified">✓</span> Solid check = Certified</li>
            <li><span className="icon verified">◻︎✓</span> Outline check = Verified by OriginSelect</li>
            <li><span className="icon self-reported">ℹ︎</span> Info icon = Self-reported</li>
            <li><span className="icon pending">⏱</span> Clock = Pending</li>
            <li><span className="icon expired">⚠︎</span> Warning = Expired</li>
          </ul>
          <p className="display-note">
            We show: "Verified as of {'{month YYYY}'} · Policy" with a link to this page.
          </p>
        </section>

        <section className="claims-section">
          <h2>Claims we review and typical evidence</h2>
          <p className="note">
            <em>We accept redacted documents. You may hide pricing, account numbers, or other sensitive fields 
            as long as the verifying elements remain visible.</em>
          </p>

          <h3>OriginSelect Canada's Core Values</h3>
          <p>
            Our Canadian marketplace organizes brands around verified values that conscious consumers care about:
          </p>
          <div className="values-grid">
            <ul className="values-column">
              <li><strong>Women-Owned</strong></li>
              <li><strong>Indigenous-Owned</strong></li>
              <li><strong>Veteran-Owned</strong></li>
              <li><strong>LGBTQ+ Owned</strong></li>
              <li><strong>Minority-Owned</strong></li>
            </ul>
            <ul className="values-column">
              <li><strong>Family-Owned</strong></li>
              <li><strong>Sustainable</strong></li>
              <li><strong>B-Corp Certified</strong></li>
              <li><strong>Organic</strong></li>
              <li><strong>Non-GMO</strong></li>
            </ul>
            <ul className="values-column">
              <li><strong>Vegan</strong></li>
              <li><strong>Fair Trade</strong></li>
              <li><strong>Local Community</strong></li>
              <li><strong>Fully Canadian</strong></li>
              <li><strong>Made in Canada</strong></li>
            </ul>
          </div>

          <h3>Canadian location verification</h3>
          
          <div className="claim-type">
            <h4>Province/Territory Verification</h4>
            <p><strong>Acceptable:</strong> Business registration documents showing province/territory of incorporation or principal place of business; 
            provincial business registry records; official business license with province designation; public company filings (provincial business databases).</p>
            <p><em>We verify the Canadian province/territory where the brand is headquartered or primarily operates to help shoppers discover local businesses in their region.</em></p>
          </div>

          <div className="claim-type">
            <h4>Fully Canadian</h4>
            <p><strong>Definition:</strong> Product is designed, manufactured, and packaged entirely in Canada using predominantly Canadian materials and labor.</p>
            <p><strong>Acceptable:</strong> Manufacturing documentation showing Canadian facilities; supply chain documentation; product packaging/labels showing "Made in Canada"; 
            Canada Brand certification; provincial/federal Made in Canada verification; detailed attestation with supporting documentation.</p>
            <p><em>This is our highest standard for Canadian authenticity and requires comprehensive verification.</em></p>
          </div>

          <div className="claim-type">
            <h4>Made in Canada / Canadian Manufacturing</h4>
            <p><strong>Definition:</strong> Product is assembled or substantially transformed in Canada, though some materials may be imported.</p>
            <p><strong>Acceptable:</strong> Manufacturing location documentation; Canada Brand certification; product labels; 
            attestation with supporting details about Canadian manufacturing processes.</p>
          </div>

          <h3>Ownership & identity claims</h3>

          <div className="claim-type">
            <h4>Women-Owned (also "Women-Led")</h4>
            <p><strong>Acceptable:</strong> WBE Canada certification or directory entry; provincial/federal women-owned business certification; 
            legal docs showing ≥51% female ownership & control; public registry or press with ownership breakdown.</p>
          </div>

          <div className="claim-type">
            <h4>Indigenous-Owned</h4>
            <p><strong>Acceptable:</strong> CCAB (Canadian Council for Aboriginal Business) certification; 
            CAMSC (Canadian Aboriginal and Minority Supplier Council) certification; 
            NACCA (National Aboriginal Capital Corporations Association) documentation;
            tribal/band business registration; legal docs showing ≥51% Indigenous ownership & control; 
            status card verification combined with ownership documents.</p>
            <p><em>We recognize First Nations, Métis, and Inuit ownership. Indigenous-owned businesses are a priority for verification 
            given their cultural and economic significance in Canada.</em></p>
          </div>

          <div className="claim-type">
            <h4>Minority-Owned / LGBTQ+ Owned</h4>
            <p><strong>Acceptable:</strong> CAMSC certification; provincial supplier diversity certification; 
            CGLCC (Canadian Gay & Lesbian Chamber of Commerce) certification (for LGBTQ+ Owned); 
            legal docs showing ≥51% ownership & control; public registry/press.</p>
          </div>

          <div className="claim-type">
            <h4>Veteran-Owned</h4>
            <p><strong>Acceptable:</strong> Canadian Veteran-Owned Business (CVOB) certification or equivalent; 
            Veterans Affairs Canada documentation; provincial veteran business program listing; 
            legal docs & veteran status proof (CF-98 Release or equivalent service records).</p>
          </div>

          <div className="claim-type">
            <h4>Family-Owned</h4>
            <p><strong>Acceptable:</strong> Business documents showing family members as primary owners/operators; articles of incorporation; 
            partnership agreements; public statements or press coverage confirming family ownership structure.</p>
          </div>

          <p className="note">
            <strong>Notes:</strong> Where certification programs exist, we prefer them. Where they don't, we accept 
            reasonable documentary proof of ownership and control (not only employment mix).
          </p>

          <h3>Sustainability & social certifications</h3>

          <ul className="certification-list">
            <li><strong>B-Corp Certified</strong> — confirmed via the B Lab public directory or a valid certificate.</li>
            <li><strong>Canada Organic</strong> — confirmed via the CFIA Organic Product Regulations or a valid certificate from an accredited certification body (CFIA-recognized).</li>
            <li><strong>Fair Trade Canada</strong> — confirmed via Fair Trade Canada registry or certificate.</li>
            <li><strong>Sustainable</strong> — confirmed via recognized sustainability certifications (Climate Neutral, Carbon Neutral, Forest Stewardship Council (FSC), EcoLogo, etc.) or comprehensive sustainability reports with third-party verification.</li>
            <li><strong>Leaping Bunny / Cruelty-Free</strong> — confirmed via program registry or certificate.</li>
            <li><strong>Non-GMO Project Verified</strong> — confirmed via program registry or certificate.</li>
            <li><strong>Gluten-Free / Vegan</strong> — confirmed where a recognized certifier exists; otherwise treated as Self-reported unless product packaging and COAs conclusively support the claim.</li>
            <li><strong>Ocean Wise</strong> — confirmed via Ocean Wise Seafood registry for sustainable seafood products.</li>
            <li><strong>Canada GAP</strong> — confirmed for food safety certification in Canadian agriculture.</li>
          </ul>
        </section>

        <section className="editorial-section">
          <h2>Claims we treat as editorial/self-reported</h2>
          <p>
            "Clean," "Non-toxic," "Sustainable" (without a cert), "Eco-friendly," "Natural," "Artisan" (without documentation).
          </p>
          <p>
            We may include these as tags for discovery but display them as <strong>Self-reported</strong> unless 
            supported by a recognized standard.
          </p>
        </section>

        <section className="request-section">
          <h2>How to request verification</h2>
          <p>
            Submit via the brand's Contact card or email{' '}
            <a href="mailto:admin@originselect.com">admin@originselect.com</a> with:
          </p>
          <ul>
            <li>Brand name + URL on OriginSelect</li>
            <li>Claim(s) you want verified</li>
            <li>Evidence links (public registry page) and/or files (PDF/JPG/PNG)</li>
            <li>Contact name, role, and email</li>
            <li>Optional: expiry/renewal dates for certifications</li>
          </ul>

          <h3>Review</h3>
          <p>
            We check authenticity against public registries (including Canadian federal and provincial databases) and/or evaluate the documents.
          </p>
          <p>
            <strong>Pro brands receive priority review, but outcomes are independent of plan.</strong>
          </p>

          <h3>Outcome</h3>
          <ul>
            <li><strong>Approved</strong> → badge shows as Certified or Verified by OriginSelect with a "Verified as of" date.</li>
            <li><strong>More info needed</strong> → we'll request specific items.</li>
            <li><strong>Not approved</strong> → we'll explain why and keep the claim as Self-reported.</li>
          </ul>

          <p className="highlight-note">
            <strong>We do not charge for verification.</strong> Pro plan fees are for profile features and review priority, 
            not for badge outcomes.
          </p>
        </section>

        <section className="reverification-section">
          <h2>Reverification, expiry, and changes</h2>
          <ul>
            <li><strong>Recheck cadence:</strong> At least annually for active brands, or sooner if we become aware of a change.</li>
            <li><strong>Certification expiry:</strong> When a certificate includes an expiration date, we align the badge status with that date.</li>
            <li><strong>Material changes:</strong> If ownership/control changes or a certification is suspended/revoked, notify us within 30 days at <a href="mailto:admin@originselect.com">admin@originselect.com</a>.</li>
            <li><strong>Downgrades:</strong> If a brand downgrades from Pro to Free, verified badges remain unless they expire or are revoked.</li>
          </ul>
        </section>

        <section className="appeals-section">
          <h2>Appeals & corrections</h2>
          <p>If you believe we made an error:</p>
          <ol>
            <li>Reply to our email thread or contact <a href="mailto:admin@originselect.com">admin@originselect.com</a> with "Appeal" in the subject line.</li>
            <li>Provide clarifying documents or updated links.</li>
            <li>We'll re-review and update the badge or explanation.</li>
          </ol>
        </section>

        <section className="privacy-section">
          <h2>Privacy & data handling</h2>
          <ul>
            <li>We store only what's needed to substantiate the badge: org name, claim, verifying body, dates, links, and reviewer notes.</li>
            <li>Sensitive files may be deleted after review; redactions are encouraged.</li>
            <li>We do not publish your private documents.</li>
            <li>By submitting documents, you confirm you have authority to share them for verification.</li>
            <li>We comply with Canadian privacy laws including PIPEDA (Personal Information Protection and Electronic Documents Act).</li>
          </ul>
        </section>

        <section className="misuse-section">
          <h2>Misuse & enforcement</h2>
          <p>
            Submitting falsified documents or misrepresenting ownership/certifications may lead to badge removal, 
            account restrictions, and/or a brand profile takedown.
          </p>
          <p>
            We reserve the right to add contextual notes to protect shoppers (e.g., "Certification under review").
          </p>
        </section>

        <section className="limitations-section">
          <h2>Limitations & disclaimer</h2>
          <ul>
            <li>OriginSelect is not a certifying body. We rely on public registries, official documentation, and good-faith submissions.</li>
            <li>We do our best to be accurate but can't guarantee completeness; shoppers should always check packaging and the brand's official channels for the latest status.</li>
            <li>Inclusion on OriginSelect does not imply endorsement.</li>
            <li>OriginSelect operates in compliance with Canadian consumer protection laws and Competition Bureau guidelines.</li>
          </ul>
        </section>

        <section className="icon-key-section">
          <h2>Icon & label key (for your UI)</h2>
          <table className="icon-key-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Icon</th>
                <th>Display</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Certified</td>
                <td className="icon-cell"><span className="icon certified">✓</span></td>
                <td>Solid ✓ & program name</td>
              </tr>
              <tr>
                <td>Verified by OriginSelect</td>
                <td className="icon-cell"><span className="icon verified">◻︎✓</span></td>
                <td>Outline ✓ & claim</td>
              </tr>
              <tr>
                <td>Self-reported</td>
                <td className="icon-cell"><span className="icon self-reported">ℹ︎</span></td>
                <td>ℹ︎ tag</td>
              </tr>
              <tr>
                <td>Pending</td>
                <td className="icon-cell"><span className="icon pending">⏱</span></td>
                <td>⏱ tag</td>
              </tr>
              <tr>
                <td>Expired</td>
                <td className="icon-cell"><span className="icon expired">⚠︎</span></td>
                <td>⚠︎ tag</td>
              </tr>
            </tbody>
          </table>
          <p className="tooltip-note">
            <em>Each badge shows a tooltip: "What this means: {'{Certified|Verified|Self-reported}'}. Source: {'{Registry name | Documents on file}'}. As of: {'{MMM YYYY}'}"</em>
          </p>
        </section>

        <section className="changelog-section">
          <h2>Changelog</h2>
          <p>
            <strong>v1.0 (Nov 2025):</strong> Initial public policy for Canadian marketplace. Supports ownership (Women-Owned, Indigenous-Owned, 
            Minority-Owned, Veteran-Owned) and certifications (B-Corp, Canada Organic, Fair Trade Canada, Carbon/Climate Neutral, Cruelty-Free, 
            Non-GMO, Gluten-Free/Vegan where certified). Adds Canadian-specific location verification including Fully Canadian, Made in Canada, 
            and provincial verification.
          </p>
        </section>

        <section className="contact-section">
          <h2>Questions?</h2>
          <p>
            For verification requests, appeals, or questions about this policy, email{' '}
            <a href="mailto:admin@originselect.com">admin@originselect.com</a>
          </p>
        </section>

        <div className="back-link">
          <Link to="/country/canada">← Back to Home</Link>
        </div>
      </div>
    </div>
  );
}

export default VerificationPolicy;
